import React, { useState, useRef, useEffect } from 'react';
import './BulkUploadRepoteesModal.css';

interface Assessment {
  assessmentId: number;
  assessmentTitle: string;
  status: string;
}

interface BulkUploadRepoteesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BulkReporteeRelationshipData[], assessmentId?: number) => Promise<void>;
  assessments?: Assessment[];
  defaultAssessmentId?: number;
}

export interface BulkReporteeRelationshipData {
  employee_name: string;
  employee_email: string;
  reportee_name: string;
  reportee_email: string;
}

const BulkUploadRepoteesModal: React.FC<BulkUploadRepoteesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  assessments = [],
  defaultAssessmentId
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<BulkReporteeRelationshipData[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<number | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize assessment dropdown with default when modal opens
  useEffect(() => {
    if (isOpen && defaultAssessmentId) {
      setSelectedAssessmentId(defaultAssessmentId);
    }
  }, [isOpen, defaultAssessmentId]);

  const readyAssessments = assessments.filter(a => a.status === 'ready');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const parseCSV = (text: string): BulkReporteeRelationshipData[] => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().toLowerCase());

    // Support two formats:
    // 1. Single: employee_name, employee_email, reportee_name, reportee_email
    // 2. Multiple: employee_name, employee_email, reportee_names, reportee_emails (semicolon-separated)
    const isSingleFormat = header.includes('reportee_name') && header.includes('reportee_email');
    const isMultipleFormat = header.includes('reportee_names') && header.includes('reportee_emails');

    if (!isSingleFormat && !isMultipleFormat) {
      throw new Error('CSV must contain either (reportee_name, reportee_email) OR (reportee_names, reportee_emails) columns');
    }

    if (!header.includes('employee_name') || !header.includes('employee_email')) {
      throw new Error('CSV must contain employee_name and employee_email columns');
    }

    // Get column indices
    const employeeNameIdx = header.indexOf('employee_name');
    const employeeEmailIdx = header.indexOf('employee_email');
    const reporteeNameIdx = isSingleFormat ? header.indexOf('reportee_name') : header.indexOf('reportee_names');
    const reporteeEmailIdx = isSingleFormat ? header.indexOf('reportee_email') : header.indexOf('reportee_emails');

    // Parse data rows
    const data: BulkReporteeRelationshipData[] = [];
    const parseErrors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const values = line.split(',').map(v => v.trim());

      if (values.length < 4) {
        parseErrors.push(`Row ${i + 1}: Insufficient columns`);
        continue;
      }

      const employeeName = values[employeeNameIdx];
      const employeeEmail = values[employeeEmailIdx];
      const reporteeNamesStr = values[reporteeNameIdx];
      const reporteeEmailsStr = values[reporteeEmailIdx];

      // Validate employee data
      if (!employeeName) {
        parseErrors.push(`Row ${i + 1}: Employee name is required`);
        continue;
      }
      if (!employeeEmail || !validateEmail(employeeEmail)) {
        parseErrors.push(`Row ${i + 1}: Invalid employee email: ${employeeEmail}`);
        continue;
      }

      // Parse reportees (single or multiple)
      const reporteeNames = reporteeNamesStr ? reporteeNamesStr.split(';').map(n => n.trim()) : [];
      const reporteeEmails = reporteeEmailsStr ? reporteeEmailsStr.split(';').map(e => e.trim()) : [];

      // Validate reportee data
      if (reporteeNames.length === 0 || reporteeEmails.length === 0) {
        parseErrors.push(`Row ${i + 1}: At least one reportee is required`);
        continue;
      }

      if (reporteeNames.length !== reporteeEmails.length) {
        parseErrors.push(`Row ${i + 1}: Number of reportee names (${reporteeNames.length}) must match number of reportee emails (${reporteeEmails.length})`);
        continue;
      }

      // Validate each reportee
      let hasErrors = false;
      for (let j = 0; j < reporteeEmails.length; j++) {
        if (!reporteeNames[j]) {
          parseErrors.push(`Row ${i + 1}, Reportee ${j + 1}: Name is required`);
          hasErrors = true;
        }
        if (!reporteeEmails[j] || !validateEmail(reporteeEmails[j])) {
          parseErrors.push(`Row ${i + 1}, Reportee ${j + 1}: Invalid email: ${reporteeEmails[j]}`);
          hasErrors = true;
        }
      }

      if (hasErrors) {
        continue;
      }

      // Create relationship objects for each reportee
      for (let j = 0; j < reporteeNames.length; j++) {
        data.push({
          employee_name: employeeName,
          employee_email: employeeEmail,
          reportee_name: reporteeNames[j],
          reportee_email: reporteeEmails[j]
        });
      }
    }

    if (parseErrors.length > 0) {
      setErrors(parseErrors);
    }

    return data;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setErrors(['Please select a valid CSV file']);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setParsedData([]);

    // Read and parse the file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data = parseCSV(text);
        setParsedData(data);
      } catch (error: any) {
        setErrors([error.message || 'Failed to parse CSV file']);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      setErrors(['No valid data to upload']);
      return;
    }

    setUploading(true);
    try {
      await onSubmit(parsedData, selectedAssessmentId);
      handleClose();
    } catch (error: any) {
      setErrors([error.message || 'Failed to upload reportee relationships']);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setUploading(false);
    setSelectedAssessmentId(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const template = 'employee_name,employee_email,reportee_names,reportee_emails\n' +
                     'John Doe,john.doe@example.com,Alice Johnson;Bob Wilson,alice.johnson@example.com;bob.wilson@example.com\n' +
                     'Jane Smith,jane.smith@example.com,Charlie Brown,charlie.brown@example.com\n' +
                     'Mike Davis,mike.davis@example.com,Diana Prince;Eve Adams;Frank Moore,diana.prince@example.com;eve.adams@example.com;frank.moore@example.com';

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'reportee_relationships_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="bulk-upload-reportees-modal-overlay" onClick={handleClose}>
      <div className="bulk-upload-reportees-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bulk-upload-reportees-modal-header">
          <h3>Bulk Upload Reportee Relationships</h3>
          <button className="bulk-upload-reportees-modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="bulk-upload-reportees-modal-body">
          {/* Instructions */}
          <div className="bulk-upload-reportees-instructions">
            <h4>Instructions:</h4>
            <ol>
              <li>Download the CSV template or prepare your own CSV file</li>
              <li>Fill in the required columns: <strong>employee_name, employee_email, reportee_names, reportee_emails</strong></li>
              <li>For multiple reportees per employee, separate names and emails with semicolons (;)</li>
              <li>Optionally select an assessment to assign users to</li>
              <li>Upload the completed CSV file</li>
              <li>Review the parsed data and click "Upload" to create relationships</li>
            </ol>
            <p className="bulk-upload-reportees-note">
              <strong>Examples:</strong><br/>
              • Single reportee: <code>John Doe, john@example.com, Alice Johnson, alice@example.com</code><br/>
              • Multiple reportees: <code>John Doe, john@example.com, Alice;Bob;Charlie, alice@example.com;bob@example.com;charlie@example.com</code>
            </p>
            <p className="bulk-upload-reportees-note">
              <strong>Note:</strong> New users will be automatically onboarded and receive a welcome email with login credentials.
              If an assessment is selected, all users will be assigned to it.
            </p>
          </div>

          {/* Assessment Selection (Optional) */}
          <div className="bulk-upload-reportees-option-section">
            <label className="bulk-upload-reportees-option-label">
              Assign to Assessment (Optional)
            </label>
            <select
              className="bulk-upload-reportees-select"
              value={selectedAssessmentId || ''}
              onChange={(e) => setSelectedAssessmentId(e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={uploading}
            >
              <option value="">-- No Assessment --</option>
              {readyAssessments.map((assessment) => (
                <option key={assessment.assessmentId} value={assessment.assessmentId}>
                  {assessment.assessmentTitle}
                </option>
              ))}
            </select>
          </div>

          {/* Download Template Button */}
          <button
            type="button"
            className="bulk-upload-reportees-btn-template"
            onClick={downloadTemplate}
          >
            Download CSV Template
          </button>

          {/* File Upload */}
          <div className="bulk-upload-reportees-file-section">
            <label htmlFor="csvFile" className="bulk-upload-reportees-file-label">
              Select CSV File *
            </label>
            <input
              ref={fileInputRef}
              id="csvFile"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={uploading}
              className="bulk-upload-reportees-file-input"
            />
            {file && (
              <div className="bulk-upload-reportees-file-info">
                <span className="bulk-upload-reportees-file-name">{file.name}</span>
                <span className="bulk-upload-reportees-file-size">
                  ({(file.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bulk-upload-reportees-errors">
              <h4>Validation Errors:</h4>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview Table */}
          {parsedData.length > 0 && (
            <div className="bulk-upload-reportees-preview">
              <h4>Preview ({parsedData.length} relationships)</h4>
              <div className="bulk-upload-reportees-table-wrapper">
                <table className="bulk-upload-reportees-preview-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Employee Name</th>
                      <th>Employee Email</th>
                      <th>Reportee Name</th>
                      <th>Reportee Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 10).map((row, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{row.employee_name}</td>
                        <td>{row.employee_email}</td>
                        <td>{row.reportee_name}</td>
                        <td>{row.reportee_email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <p className="bulk-upload-reportees-preview-note">
                    Showing first 10 of {parsedData.length} relationships
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bulk-upload-reportees-modal-footer">
          <button
            className="bulk-upload-reportees-btn-secondary"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            className="bulk-upload-reportees-btn-primary"
            onClick={handleSubmit}
            disabled={uploading || parsedData.length === 0}
          >
            {uploading ? 'Uploading...' : `Upload ${parsedData.length} Relationship${parsedData.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadRepoteesModal;
